import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from "@angular/core";
import {createEvent} from "../misc/event.polyfill";
import {FormControl} from "@angular/forms";
import {DialogService} from "../bootstrap-modal/dialog.service";
import {SearchHelpDialogComponent} from "./search-help-dialog.component";
import {debounceTime} from "rxjs/operators";

const safeParseInt = i => (Number.isNaN(i) ? 0 : parseInt(i, 10));

const nvl = (str, replace = "") => (str === null || str === undefined ? replace : str);

@Component({
  selector: "list-controls",
  templateUrl: "./list-controls.template.pug",
})
export class ListControlsComponent implements OnInit, OnChanges {
  allowedPageSizes: number[] = [5, 10, 20, 50, 100];

  pageList: {page: number, active: boolean}[];
  canPreviousPage: boolean;
  canNextPage: boolean;

  page: number;
  numPages: number;
  limit: number;
  totalCount: number;
  zero: boolean;
  start: number;
  finish: number;
  search: string;
  hasSearchHelp: boolean = false;

  searchControl: FormControl = new FormControl();

  @Input("page")
  _page: any;

  @Input("numPages")
  _numPages: any;

  @Input("limit")
  _limit: any;

  @Input("totalCount")
  _totalCount: any;

  @Input("search")
  _search: any;

  @Input("searchHelp")
  searchHelp: any;

  @Input("showSearch")
  showSearch: any = true;

  @Input("showLimit")
  showLimit: any = true;

  @Output("onChange")
  public onChangeEmitter: any = new EventEmitter();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["_search"]) {
      this.search = nvl(changes["_search"].currentValue);
    }

    if (changes["_numPages"]) {
      this.numPages = Math.max(1, safeParseInt(changes["_numPages"].currentValue));
    }

    if (changes["_page"]) {
      this.page = Math.max(1, safeParseInt(changes["_page"].currentValue));
    }

    if (changes["_limit"]) {
      this.limit = Math.max(1, safeParseInt(changes["_limit"].currentValue));
    }

    if (changes["_totalCount"]) {
      this.totalCount = safeParseInt(changes["_totalCount"].currentValue);
    }

    const invalidPageData = [this.numPages, this.totalCount, this.limit, this.page]
      .some(it => Number.isNaN(+it) || +it <= 0);

    if (invalidPageData) {
      this.pageList = [];
      this.zero = true;
      this.start = 0;
      this.finish = 0;
      this.canPreviousPage = false;
      this.canNextPage = false;
      return;
    }

    this.canPreviousPage = this.page > 1 && this.page <= this.numPages;
    this.canNextPage = this.page < this.numPages;

    const distanceFromEnd = Math.max(2 - (this.numPages - this.page), 0);
    const min = Math.max(this.page - (2 + distanceFromEnd), 1);
    this.pageList = [];

    for (let i = 0; i < 5; i++) {
      if (min + i > this.numPages) break;
      this.pageList.push({page: min + i, active: this.page === min + i});
    }

    this.start = (this.limit * (this.page - 1)) + 1;
    this.finish = Math.min(this.totalCount, this.start + this.limit - 1);

    this.zero = (this.totalCount === 0);
  }

  ngOnInit() {
    this.ngOnChanges({});

    this.searchControl.valueChanges
      .pipe(
        debounceTime(1000),
      )
      .subscribe(newValue => this.change("search", newValue));

    this.hasSearchHelp = this.searchHelp && this.searchHelp.length && this.searchHelp.length > 0;
  }

  goToPage(newPage: any): void {
    this.page = newPage;
    this.change("page", newPage);
  }

  previousPage(): void {
    this.goToPage(this.page - 1);
  }

  nextPage(): void {
    this.goToPage(this.page + 1);
  }

  searchSubmit(event) {
    if (event.type !== "click" && event.keyCode !== 13) return;
    this.search = this.searchControl.value;
    this.change("search", this.search);

    event.stopPropagation();
    event.preventDefault();
  }

  limitChanged() {
    this.change("limit", this.limit);
  }

  change(key, value) {
    const event = createEvent("change");
    event["key"] = key;
    event["value"] = value;
    this.onChangeEmitter.emit(event);
  }

  openSearchHelp() {
    SearchHelpDialogComponent.show(this.dialogService, {searchOptions: this.searchHelp});
  }

  constructor(public dialogService: DialogService) {
    this.zero = true;
  }
}
